import { ConfirmationDialog } from '@/components/patterns/confirmation-dialog'
import { AdminHeaderActionButton, AdminPageHeader } from '@/components/patterns/admin-page'
import { OrganizationContextNotice } from '@/components/patterns/info-notice'
import { EmptyState } from '@/components/patterns/empty-state'
import {
  IndexDataTable,
  IndexTableClearFiltersButton,
  IndexTableSearchBox,
  IndexTableToolbar,
} from '@/components/tables/IndexTable'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge, BadgeButton } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { FileDropzone } from '@/components/ui/file-dropzone'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Typography } from '@/components/ui/typography'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useOrganizationContext } from '@/hooks/useOrganizationContext'
import { usePathHelper } from '@/hooks/usePathHelper'
import { OrganizationType } from '@/lib/enums/OrganizationType'
import {
  invalidateProductImportCatalogQueries,
  useActiveProductImportAttemptQuery,
  useImportProductImportMutation,
  usePreviewProductImportMutation,
  useProductImportAttemptQuery,
} from '@/lib/services/imports/imports.query'
import { productImportRequestSchema } from '@/lib/services/imports/imports.schema'
import { cn, getErrorMessage } from '@/lib/utils'
import { createFileRoute, Link } from '@tanstack/react-router'
import { AlertCircle, ArrowLeft, PackageOpen, SlidersHorizontal, Upload } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

const productSourceOptions = [{ value: 'fsn', label: 'FSN' }]
const importScopeOptions = [
  { value: 'active_pages', label: 'Products on active pages' },
  { value: 'all_active', label: 'All active products' },
]
const websiteNavigationOptions = [
  { value: 'auto', label: 'Set up menu' },
  { value: 'skip', label: 'Leave as-is' },
]
const expectedColumnLabels = [
  'ProductId',
  'ArrangementName',
  'ArrangementLongName',
  'ArrangementType',
  'ProductCategory',
  'ProductOccasions',
  'Categories',
  'CollectionPageIds',
  'CollectionNames',
  'CollectionPagePaths',
  'CollectionPageSequences',
  'CollectionPlacementsJson',
  'CollectionOccasions',
  'CollectionBannerImageUrls',
  'Description',
  'StandardPrice',
  'DeluxePrice',
  'PremiumPrice',
  'ShownAtPrice',
  'PriceSource',
  'DisplayStandard',
  'DisplayDeluxe',
  'DisplayPremium',
  'DisplayShownAt',
  'ImageUrl',
  'Recipe',
  'IsDesignersChoice',
]
const previewDefaultPageSize = 50

function createImportAttemptId() {
  if (globalThis.crypto?.randomUUID) {
    return globalThis.crypto.randomUUID()
  }

  const randomHex = (length) =>
    Array.from({ length }, () => Math.floor(Math.random() * 16).toString(16)).join('')

  return `${randomHex(8)}-${randomHex(4)}-4${randomHex(3)}-${(
    8 + Math.floor(Math.random() * 4)
  ).toString(16)}${randomHex(3)}-${randomHex(12)}`
}

const statusBadgeVariant = {
  ready: 'info',
  imported: 'success',
  updated: 'success',
  skipped: 'warning',
  invalid: 'destructive',
}

const statusBadgeLabel = {
  ready: 'Ready',
  imported: 'Imported',
  updated: 'Updated',
  skipped: 'Skipped',
  invalid: 'Invalid',
}

const websiteNavigationBadgeVariant = {
  pending: 'secondary',
  updated: 'success',
  unchanged: 'info',
  skipped: 'warning',
}

const websiteNavigationStatusLabel = {
  pending: 'After import',
  updated: 'Updated',
  unchanged: 'Already set',
  skipped: 'Skipped',
}

const statusTooltipVariant = {
  skipped: 'warning',
  invalid: 'destructive',
}

const previewStatusFilterOptions = [
  { value: 'all', label: 'All statuses', icon: <SlidersHorizontal className="size-4" /> },
  { value: 'ready', label: 'Ready' },
  { value: 'imported', label: 'Imported' },
  { value: 'updated', label: 'Updated' },
  { value: 'skipped', label: 'Skipped' },
  { value: 'invalid', label: 'Invalid' },
]

export const Route = createFileRoute('/_authenticated/$orgId/imports/product-import')({
  component: ProductImportPage,
})

function SummaryCard({ label, value }) {
  return (
    <Card>
      <CardHeader className="space-y-1">
        <CardDescription>{label}</CardDescription>
        <CardTitle className="text-3xl">{value}</CardTitle>
      </CardHeader>
    </Card>
  )
}

function ProductImportProgressCard({ attempt }) {
  if (!attempt) {
    return null
  }

  const isFailed = attempt.status === 'failed'
  const isCompleted = attempt.status === 'completed'
  const progressUnit = attempt.stage === 'preparing_images' ? 'photos' : 'products'
  const progressLabel =
    attempt.progress.total > 0
      ? `${attempt.progress.processed} of ${attempt.progress.total} ${progressUnit}`
      : 'Preparing import'
  const title = isCompleted ? 'Import Complete' : isFailed ? 'Import Failed' : 'Import Running'

  return (
    <Card surface={isFailed ? 'destructive' : 'default'}>
      <CardHeader className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <CardTitle>{title}</CardTitle>
            <CardDescription>{attempt.message ?? attempt.stage_label}</CardDescription>
          </div>
          <Badge variant={isCompleted ? 'success' : isFailed ? 'destructive' : 'info'}>
            {attempt.stage_label}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div
          className="h-3 overflow-hidden rounded-full bg-muted"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={attempt.progress.percent}
          aria-label="Product import progress"
        >
          <div
            className={cn(
              'h-full rounded-full transition-[width] duration-300',
              isFailed ? 'bg-destructive' : 'bg-primary'
            )}
            style={{ width: `${attempt.progress.percent}%` }}
          />
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-muted-foreground">
          <span>{progressLabel}</span>
          <span>{attempt.progress.percent}%</span>
        </div>
        {attempt.error?.message ? (
          <p className="text-sm text-destructive">{attempt.error.message}</p>
        ) : null}
      </CardContent>
    </Card>
  )
}

function ImageFailuresCard({ failures }) {
  if (!failures?.length) {
    return null
  }

  return (
    <Card surface="warning">
      <CardHeader>
        <CardTitle>Image Issues</CardTitle>
        <CardDescription>These products imported, but their photos did not.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="divide-y divide-border/70 rounded-md border border-border/70 bg-background/50">
          {failures.map((failure) => {
            const productName = failure.name?.trim() || failure.source_key?.trim() || 'Product'
            const rowLabel = failure.line_number ? `Row ${failure.line_number}` : 'Row unknown'
            const sourceKey = failure.source_key?.trim() ? `Product ID ${failure.source_key}` : null

            return (
              <div
                key={`${failure.line_number}-${failure.source_key}-${failure.image_url}`}
                className="space-y-1 px-3 py-2"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <Typography variant="labelStrong">{productName}</Typography>
                  <Badge variant="warning">{rowLabel}</Badge>
                  {sourceKey ? (
                    <span className="text-xs text-muted-foreground">{sourceKey}</span>
                  ) : null}
                </div>
                <p className="text-sm text-muted-foreground">{failure.reason}</p>
                {failure.image_url ? (
                  <p className="break-all text-xs text-muted-foreground">{failure.image_url}</p>
                ) : null}
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}

function WebsiteNavigationCard({ report }) {
  if (!report) {
    return null
  }

  const statusLabel = websiteNavigationStatusLabel[report.status] ?? report.status
  const itemLabels = (report.items ?? []).map((item) => item.label).filter(Boolean)
  const previewLabels = itemLabels.slice(0, 8).join(', ')
  const hasMoreItems = itemLabels.length > 8

  return (
    <Card>
      <CardHeader className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle>Website Menu</CardTitle>
          <Badge variant={websiteNavigationBadgeVariant[report.status] ?? 'secondary'}>
            {statusLabel}
          </Badge>
        </div>
        <CardDescription>{report.message}</CardDescription>
      </CardHeader>
      {itemLabels.length ? (
        <CardContent>
          <Typography variant="supporting" className="block">
            First links: {previewLabels}
            {hasMoreItems ? `, and ${itemLabels.length - 8} more` : ''}
          </Typography>
        </CardContent>
      ) : null}
    </Card>
  )
}

function normalizeSearchText(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
}

function getStatusDisplayLabel(status) {
  return statusBadgeLabel[status] ?? status
}

function getIssueText(row) {
  return row.reason ?? ''
}

function formatMoney(value) {
  const amount = Number(value)

  return Number.isFinite(amount) ? `$${amount.toFixed(2)}` : ''
}

function formatVariants(variants) {
  if (!Array.isArray(variants) || variants.length === 0) {
    return 'No variants'
  }

  return variants
    .map((variant) => {
      const label = `${variant.name} ${formatMoney(variant.price)}`
      return variant.is_default ? `${label} (default)` : label
    })
    .join(', ')
}

function formatCollections(collections) {
  return collections?.length ? collections.join(', ') : 'No collections'
}

function formatPriceSource(priceSource) {
  return priceSource?.trim() ? priceSource : 'Not provided'
}

function formatShownAtPrice(price) {
  return price == null ? '' : formatMoney(price)
}

function formatCollectionSource(collectionSource) {
  if (collectionSource === 'active_pages') {
    return 'Active pages'
  }

  if (collectionSource === 'categories') {
    return 'Categories'
  }

  return 'None'
}

function getRowPreviewPresentation(row) {
  if (row.status === 'invalid') {
    return { tone: 'destructive' }
  }

  if (row.status === 'skipped') {
    return { tone: 'warning' }
  }

  if (row.status === 'imported' || row.status === 'updated') {
    return { tone: 'success' }
  }

  return { tone: 'neutral' }
}

function StatusBadgeCell({ row }) {
  const label = getStatusDisplayLabel(row.status)
  const tooltipMessage =
    row.reason && (row.status === 'invalid' || row.status === 'skipped') ? row.reason : null
  const badge = <Badge variant={statusBadgeVariant[row.status] ?? 'secondary'}>{label}</Badge>

  if (!tooltipMessage) {
    return badge
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <BadgeButton
          variant={statusBadgeVariant[row.status] ?? 'secondary'}
          aria-label={`${label}: ${tooltipMessage}`}
        >
          {label}
        </BadgeButton>
      </TooltipTrigger>
      <TooltipContent variant={statusTooltipVariant[row.status] ?? 'default'}>
        {tooltipMessage}
      </TooltipContent>
    </Tooltip>
  )
}

const previewColumns = [
  {
    accessorKey: 'row_number',
    header: 'Row',
    meta: { label: 'Row', kind: 'number' },
  },
  {
    id: 'status',
    accessorFn: (row) => getStatusDisplayLabel(row.status),
    header: 'Status',
    meta: { label: 'Status', kind: 'badge' },
    cell: ({ row }) => <StatusBadgeCell row={row.original} />,
  },
  {
    accessorKey: 'source_key',
    header: 'Product ID',
    meta: { label: 'Product ID', kind: 'text' },
  },
  {
    id: 'name',
    accessorFn: (row) => row.product.name,
    header: 'Product',
    meta: { label: 'Product', kind: 'primary', preferredWidth: '16rem', minWidth: '13rem' },
    cell: ({ row }) => row.original.product.name || 'Missing product name',
  },
  {
    id: 'action',
    accessorFn: (row) => row.action ?? '',
    header: 'Action',
    meta: { label: 'Action', kind: 'text' },
    cell: ({ row }) =>
      row.original.action === 'update'
        ? 'Update'
        : row.original.action === 'create'
          ? 'Create'
          : '',
  },
  {
    id: 'category',
    accessorFn: (row) => row.product.category_name ?? '',
    header: 'Category',
    meta: { label: 'Category', kind: 'text' },
    cell: ({ row }) => row.original.product.category_name ?? 'None',
  },
  {
    id: 'price_source',
    accessorFn: (row) =>
      [row.product.price_source, formatShownAtPrice(row.product.shown_at_price)]
        .filter(Boolean)
        .join(' '),
    header: 'Price Source',
    meta: { label: 'Price Source', kind: 'text', preferredWidth: '13rem', minWidth: '11rem' },
    cell: ({ row }) => {
      const priceSource = formatPriceSource(row.original.product.price_source)
      const shownAtPrice = formatShownAtPrice(row.original.product.shown_at_price)

      return shownAtPrice ? `${priceSource} (${shownAtPrice})` : priceSource
    },
  },
  {
    id: 'designers_choice',
    accessorFn: (row) => (row.product.is_designers_choice ? "Designer's Choice" : ''),
    header: "Designer's Choice",
    meta: { label: "Designer's Choice", kind: 'badge' },
    cell: ({ row }) =>
      row.original.product.is_designers_choice ? <Badge variant="brand">Yes</Badge> : '',
  },
  {
    id: 'variants',
    accessorFn: (row) => formatVariants(row.product.variants),
    header: 'Variants',
    meta: { label: 'Variants', kind: 'text', preferredWidth: '22rem', minWidth: '16rem' },
    cell: ({ row }) => formatVariants(row.original.product.variants),
  },
  {
    id: 'collections',
    accessorFn: (row) =>
      `${formatCollectionSource(row.product.collection_source)} ${formatCollections(row.product.collections)}`,
    header: 'Collections',
    meta: { label: 'Collections', kind: 'text', preferredWidth: '18rem', minWidth: '14rem' },
    cell: ({ row }) =>
      `${formatCollections(row.original.product.collections)} (${formatCollectionSource(row.original.product.collection_source)})`,
  },
  {
    id: 'issue',
    accessorFn: (row) => getIssueText(row),
    header: 'Issue',
    meta: { label: 'Issue', kind: 'text', preferredWidth: '22rem', minWidth: '16rem' },
    cell: ({ row }) => getIssueText(row.original),
  },
]

function matchesPreviewSearch(row, searchTerm) {
  if (!searchTerm) {
    return true
  }

  const searchableValues = [
    row.source_key,
    row.product?.name,
    row.product?.category_name,
    row.product?.price_source,
    formatShownAtPrice(row.product?.shown_at_price),
    row.product?.is_designers_choice ? "Designer's Choice" : '',
    formatVariants(row.product?.variants),
    formatCollections(row.product?.collections),
    formatCollectionSource(row.product?.collection_source),
    row.reason,
  ]

  return searchableValues.some((value) => normalizeSearchText(value).includes(searchTerm))
}

function filterPreviewRows(rows, searchTerm, statusFilter) {
  return rows.filter((row) => {
    if (statusFilter !== 'all' && row.status !== statusFilter) {
      return false
    }

    return matchesPreviewSearch(row, searchTerm)
  })
}

function getPreviewRowCountText(filteredCount, totalCount) {
  if (filteredCount === totalCount) {
    return `${totalCount} row${totalCount === 1 ? '' : 's'}`
  }

  return `${filteredCount} of ${totalCount} rows`
}

function getImportErrorDescription(error, fallbackMessage) {
  const fieldErrors = error?.response?.data?.errors
  const firstFieldMessage = fieldErrors
    ? Object.values(fieldErrors)
        .flat()
        .find((message) => typeof message === 'string' && message.length > 0)
    : null

  if (firstFieldMessage) {
    return firstFieldMessage
  }

  if (Array.isArray(error?.issues) && error.issues.length > 0) {
    return 'The import response was not readable in this browser session. Refresh the page and try again.'
  }

  const message = getErrorMessage(error)

  if (typeof message !== 'string' || message.trim() === '') {
    return fallbackMessage
  }

  return message.length > 220 ? `${message.slice(0, 217)}...` : message
}

export function ProductImportPage() {
  const { organization } = useOrganizationContext()
  const path = usePathHelper()
  const isGlobalOrg = organization?.type === OrganizationType.Global.Value
  const previewMutation = usePreviewProductImportMutation()
  const importMutation = useImportProductImportMutation()
  const [selectedFile, setSelectedFile] = useState(null)
  const [sourceSystem, setSourceSystem] = useState('fsn')
  const [importScope, setImportScope] = useState('active_pages')
  const [websiteNavigation, setWebsiteNavigation] = useState('auto')
  const [previewResult, setPreviewResult] = useState(null)
  const [previewContext, setPreviewContext] = useState(null)
  const [previewSearchTerm, setPreviewSearchTerm] = useState('')
  const [previewStatusFilter, setPreviewStatusFilter] = useState('all')
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const [errorState, setErrorState] = useState(null)
  const [activeImportAttemptId, setActiveImportAttemptId] = useState(null)
  const previewRequestVersionRef = useRef(0)
  const handledImportAttemptIdsRef = useRef(new Set())
  const activeProductImportAttemptQuery = useActiveProductImportAttemptQuery({
    enabled: Boolean(organization?.id && !isGlobalOrg),
  })
  const refetchActiveProductImportAttempt = activeProductImportAttemptQuery.refetch
  const importAttemptQuery = useProductImportAttemptQuery(activeImportAttemptId, {
    enabled: Boolean(activeImportAttemptId),
  })
  const rawRecoveredImportAttempt = activeProductImportAttemptQuery.data ?? null
  const recoveredImportAttempt =
    rawRecoveredImportAttempt?.id &&
    // oxlint-disable-next-line react/refs -- grandfathered
    handledImportAttemptIdsRef.current.has(rawRecoveredImportAttempt.id)
      ? null
      : rawRecoveredImportAttempt
  const recoveredImportAttemptId = recoveredImportAttempt?.id ?? null
  const activeImportAttempt = importAttemptQuery.data ?? null
  const importMutationAttempt =
    previewContext?.importAttemptId && importMutation.data?.id === previewContext.importAttemptId
      ? importMutation.data
      : null
  const displayedImportAttempt =
    activeImportAttempt ??
    recoveredImportAttempt ??
    (activeImportAttemptId ? importMutationAttempt : null)
  const activeImportStatus =
    activeImportAttempt?.status ?? recoveredImportAttempt?.status ?? importMutationAttempt?.status
  const isImportRunning =
    importMutation.isPending || activeImportStatus === 'queued' || activeImportStatus === 'running'

  const hasReadyRows = (previewResult?.summary?.ready ?? 0) > 0
  const hasCurrentPreview =
    hasReadyRows &&
    previewContext?.file === selectedFile &&
    previewContext?.sourceSystem === sourceSystem &&
    previewContext?.importScope === importScope &&
    previewContext?.websiteNavigation === websiteNavigation
  const previewRows = previewResult?.rows ?? []
  const previewRowsReturned = previewResult?.rows_returned ?? previewRows.length
  const previewRowsTruncated = previewResult?.rows_truncated ?? false
  const normalizedPreviewSearchTerm = normalizeSearchText(previewSearchTerm)
  const filteredPreviewRows = filterPreviewRows(
    previewRows,
    normalizedPreviewSearchTerm,
    previewStatusFilter
  )
  const hasPreviewFilters = normalizedPreviewSearchTerm !== '' || previewStatusFilter !== 'all'
  const previewTableNeedsScrollFrame = filteredPreviewRows.length > 15
  const columnsText = (previewResult?.expected_headers ?? expectedColumnLabels).join(', ')

  useEffect(() => {
    if (!recoveredImportAttemptId || activeImportAttemptId === recoveredImportAttemptId) {
      return
    }

    // oxlint-disable-next-line react/set-state-in-effect -- grandfathered
    setActiveImportAttemptId(recoveredImportAttemptId)
    setSourceSystem(recoveredImportAttempt.source_system)
    setImportScope(recoveredImportAttempt.import_scope)
    setWebsiteNavigation(recoveredImportAttempt.website_navigation_mode)
    setErrorState(null)
  }, [activeImportAttemptId, recoveredImportAttempt, recoveredImportAttemptId])

  useEffect(() => {
    if (!activeImportAttempt || handledImportAttemptIdsRef.current.has(activeImportAttempt.id)) {
      return
    }

    if (activeImportAttempt.status === 'completed') {
      handledImportAttemptIdsRef.current.add(activeImportAttempt.id)

      if (activeImportAttempt.result) {
        // oxlint-disable-next-line react/set-state-in-effect -- grandfathered
        setPreviewResult(activeImportAttempt.result)
        setPreviewSearchTerm('')
        setPreviewStatusFilter('all')
      }

      setErrorState(null)

      if (organization?.id) {
        void invalidateProductImportCatalogQueries({ organizationId: organization.id })
      }
      void refetchActiveProductImportAttempt?.()

      toast.success('Products imported.', {
        description: activeImportAttempt.result
          ? `${activeImportAttempt.result.summary.imported} created, ${activeImportAttempt.result.summary.updated} updated.`
          : 'The import finished successfully.',
      })

      return
    }

    if (activeImportAttempt.status === 'failed') {
      handledImportAttemptIdsRef.current.add(activeImportAttempt.id)
      void refetchActiveProductImportAttempt?.()
      const description =
        activeImportAttempt.error?.message ??
        activeImportAttempt.message ??
        'Please review the spreadsheet and try again.'

      setErrorState({
        title: 'Import failed',
        message: description,
      })
      toast.error('Unable to import products.', {
        description,
      })
    }
  }, [activeImportAttempt, organization?.id, refetchActiveProductImportAttempt])

  const validateSelectedFile = () =>
    productImportRequestSchema.safeParse({
      file: selectedFile,
      source_system: sourceSystem,
      import_scope: importScope,
      website_navigation: websiteNavigation,
    })
  const invalidatePreviewRequest = () => {
    previewRequestVersionRef.current += 1
  }
  const resetPreviewUi = () => {
    setPreviewResult(null)
    setPreviewContext(null)
    setPreviewSearchTerm('')
    setPreviewStatusFilter('all')
    setIsConfirmOpen(false)
    setActiveImportAttemptId(null)
  }

  const handleFileSelect = (file) => {
    const validationResult = productImportRequestSchema.safeParse({
      file,
      source_system: sourceSystem,
      import_scope: importScope,
      website_navigation: websiteNavigation,
    })

    if (!validationResult.success) {
      const message = validationResult.error.issues[0]?.message ?? 'Choose an .xlsx or .csv file.'

      invalidatePreviewRequest()
      setSelectedFile(null)
      resetPreviewUi()
      setErrorState({
        title: 'Invalid file',
        message,
      })
      toast.error(message)

      return
    }

    invalidatePreviewRequest()
    setSelectedFile(file)
    resetPreviewUi()
    setErrorState(null)
  }

  const handleFileClear = () => {
    invalidatePreviewRequest()
    setSelectedFile(null)
    resetPreviewUi()
    setErrorState(null)
  }

  const handleSourceSystemChange = (value) => {
    invalidatePreviewRequest()
    setSourceSystem(value)
    resetPreviewUi()
    setErrorState(null)
  }

  const handleImportScopeChange = (value) => {
    invalidatePreviewRequest()
    setImportScope(value)
    resetPreviewUi()
    setErrorState(null)
  }

  const handleWebsiteNavigationChange = (value) => {
    invalidatePreviewRequest()
    setWebsiteNavigation(value)
    resetPreviewUi()
    setErrorState(null)
  }

  const handlePreview = async (event) => {
    event.preventDefault()

    const validationResult = validateSelectedFile()
    if (!validationResult.success) {
      const message = validationResult.error.issues[0]?.message ?? 'Choose an .xlsx or .csv file.'

      setErrorState({
        title: 'Invalid file',
        message,
      })
      toast.error(message)

      return
    }

    const previewRequestVersion = previewRequestVersionRef.current + 1
    previewRequestVersionRef.current = previewRequestVersion
    const previewFile = selectedFile
    const previewSourceSystem = sourceSystem
    const previewImportScope = importScope
    const previewWebsiteNavigation = websiteNavigation

    try {
      const result = await previewMutation.mutateAsync({
        file: previewFile,
        sourceSystem: previewSourceSystem,
        importScope: previewImportScope,
        websiteNavigation: previewWebsiteNavigation,
      })

      if (previewRequestVersionRef.current !== previewRequestVersion) {
        return
      }

      setActiveImportAttemptId(null)
      setPreviewResult(result)
      setPreviewContext({
        file: previewFile,
        sourceSystem: previewSourceSystem,
        importScope: previewImportScope,
        websiteNavigation: previewWebsiteNavigation,
        importAttemptId: createImportAttemptId(),
      })
      setPreviewSearchTerm('')
      setPreviewStatusFilter('all')
      setErrorState(null)
      toast.success('Product import preview ready.')
    } catch (error) {
      if (previewRequestVersionRef.current !== previewRequestVersion) {
        return
      }

      const description = getImportErrorDescription(
        error,
        'Please review the spreadsheet and try again.'
      )

      setErrorState({
        title: 'Preview failed',
        message: description,
      })
      toast.error('Unable to preview product import.', {
        description,
      })
    }
  }

  const handleImport = async (event) => {
    event.preventDefault()

    const validationResult = validateSelectedFile()
    if (!validationResult.success) {
      const message = validationResult.error.issues[0]?.message ?? 'Choose an .xlsx or .csv file.'

      setErrorState({
        title: 'Invalid file',
        message,
      })
      toast.error(message)

      return
    }

    if (!hasCurrentPreview) {
      const message = 'Preview this file again before importing.'

      setIsConfirmOpen(false)
      setErrorState({
        title: 'Preview needed',
        message,
      })
      toast.error(message)

      return
    }

    const importRequestVersion = previewRequestVersionRef.current
    const importFile = selectedFile
    const importSourceSystem = sourceSystem
    const importImportScope = importScope
    const importWebsiteNavigation = websiteNavigation
    const importAttemptId = previewContext?.importAttemptId

    if (!importAttemptId) {
      const message = 'Preview this file again before importing.'

      setIsConfirmOpen(false)
      setErrorState({
        title: 'Preview needed',
        message,
      })
      toast.error(message)

      return
    }

    try {
      const attempt = await importMutation.mutateAsync({
        file: importFile,
        sourceSystem: importSourceSystem,
        importScope: importImportScope,
        websiteNavigation: importWebsiteNavigation,
        importAttemptId,
      })

      if (previewRequestVersionRef.current !== importRequestVersion) {
        return
      }

      if (attempt.status === 'completed') {
        handledImportAttemptIdsRef.current.add(attempt.id)

        if (attempt.result) {
          setPreviewResult(attempt.result)
          setPreviewSearchTerm('')
          setPreviewStatusFilter('all')
        }

        setActiveImportAttemptId(null)
        setErrorState(null)

        if (organization?.id) {
          void invalidateProductImportCatalogQueries({ organizationId: organization.id })
        }
        void refetchActiveProductImportAttempt?.()

        toast.success('Products imported.', {
          description: attempt.result
            ? `${attempt.result.summary.imported} created, ${attempt.result.summary.updated} updated.`
            : 'The import finished successfully.',
        })

        return
      }

      if (attempt.status === 'failed') {
        handledImportAttemptIdsRef.current.add(attempt.id)
        setActiveImportAttemptId(null)
        void refetchActiveProductImportAttempt?.()
        const description =
          attempt.error?.message ??
          attempt.message ??
          'Please review the spreadsheet and try again.'

        setErrorState({
          title: 'Import failed',
          message: description,
        })
        toast.error('Unable to import products.', {
          description,
        })

        return
      }

      setActiveImportAttemptId(attempt.id)
      setErrorState(null)
      toast.success('Product import started.', {
        description:
          attempt.progress.total > 0
            ? `${attempt.progress.total} product${attempt.progress.total === 1 ? ' is' : 's are'} queued for import.`
            : 'The import is queued.',
      })
    } catch (error) {
      if (previewRequestVersionRef.current !== importRequestVersion) {
        return
      }

      const description = getImportErrorDescription(
        error,
        'Please review the spreadsheet and try again.'
      )

      setErrorState({
        title: 'Import failed',
        message: description,
      })
      toast.error('Unable to import products.', {
        description,
      })
    } finally {
      setIsConfirmOpen(false)
    }
  }

  if (isGlobalOrg) {
    return (
      <div className="max-w-6xl space-y-4">
        <AdminPageHeader
          icon={PackageOpen}
          title="Product Import"
          description="Preview and import product rows into the shop you are impersonating."
          actions={
            <AdminHeaderActionButton asChild variant="outline">
              <Link {...path.imports.index().link}>
                <ArrowLeft className="size-4" />
                Back to Imports
              </Link>
            </AdminHeaderActionButton>
          }
        />

        <OrganizationContextNotice title="Choose an organization first">
          Product imports only run against the florist account you are currently impersonating.
          Choose the correct shop before you upload a file.
        </OrganizationContextNotice>
      </div>
    )
  }

  return (
    <div className="max-w-6xl space-y-4">
      <AdminPageHeader
        icon={PackageOpen}
        title="Product Import"
        description={`Preview and import products into ${organization?.name ?? 'this shop'}. Existing products are matched by their FSN product ID.`}
        actions={
          <AdminHeaderActionButton asChild variant="outline">
            <Link {...path.imports.index().link}>
              <ArrowLeft className="size-4" />
              Back to Imports
            </Link>
          </AdminHeaderActionButton>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>Upload Product File</CardTitle>
          <CardDescription>
            Use the product export for this shop. Preview the file before anything changes.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex flex-wrap gap-3">
            <div className="space-y-1">
              <label className="text-sm font-medium" htmlFor="product-import-source">
                Source
              </label>
              <Select value={sourceSystem} onValueChange={handleSourceSystemChange}>
                <SelectTrigger
                  id="product-import-source"
                  aria-label="Product source"
                  size="toolbar"
                  className="w-24"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {productSourceOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium" htmlFor="product-import-scope">
                Product set
              </label>
              <Select value={importScope} onValueChange={handleImportScopeChange}>
                <SelectTrigger
                  id="product-import-scope"
                  aria-label="Product set"
                  size="toolbar"
                  className="w-56"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {importScopeOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium" htmlFor="product-import-website-menu">
                Website menu
              </label>
              <Select value={websiteNavigation} onValueChange={handleWebsiteNavigationChange}>
                <SelectTrigger
                  id="product-import-website-menu"
                  aria-label="Website menu"
                  size="toolbar"
                  className="w-36"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {websiteNavigationOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <FileDropzone
            id="product-import-file"
            label="Product import file (.xlsx or .csv)"
            accept=".xlsx,.csv"
            file={selectedFile}
            onFileSelect={handleFileSelect}
            onClear={handleFileClear}
            description="XLSX or CSV. The first worksheet is read for spreadsheets."
          />

          <details className="border-t border-border/70 pt-3">
            <summary className="cursor-pointer text-sm font-medium text-muted-foreground hover:text-foreground">
              Show file columns
            </summary>
            <Typography variant="supporting" className="mt-2 block [overflow-wrap:anywhere]">
              {columnsText}
            </Typography>
          </details>

          {errorState ? (
            <Alert variant="destructive">
              <AlertCircle />
              <AlertTitle>{errorState.title}</AlertTitle>
              <AlertDescription>
                <p>{errorState.message}</p>
              </AlertDescription>
            </Alert>
          ) : null}

          {previewResult?.summary?.image_failures ? (
            <Alert variant="warning">
              <AlertCircle />
              <AlertTitle>Some images were not imported</AlertTitle>
              <AlertDescription>
                Products with image problems were imported without photos. Check the image issues
                below.
              </AlertDescription>
            </Alert>
          ) : null}

          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              onClick={handlePreview}
              disabled={!selectedFile || previewMutation.isPending || isImportRunning}
            >
              <Upload className="size-4" />
              {previewMutation.isPending ? 'Previewing...' : 'Preview Import'}
            </Button>

            <ConfirmationDialog
              open={isConfirmOpen}
              onOpenChange={setIsConfirmOpen}
              isPending={importMutation.isPending}
              variant="question"
              title="Import Products"
              description={
                websiteNavigation === 'auto'
                  ? 'Create ready products, update matching product IDs, and set up the website menu when it is safe to do so. Products missing from this file stay untouched.'
                  : 'Create ready products and update matching product IDs. Products missing from this file stay untouched.'
              }
              confirmText="Import Products"
              pendingText="Starting..."
              onConfirm={handleImport}
              trigger={
                <Button
                  type="button"
                  disabled={!hasCurrentPreview || previewMutation.isPending || isImportRunning}
                >
                  <PackageOpen className="size-4" />
                  {isImportRunning ? 'Import Running' : 'Import Products'}
                </Button>
              }
            />
          </div>
        </CardContent>
      </Card>

      <ProductImportProgressCard attempt={displayedImportAttempt} />

      {previewResult ? (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <SummaryCard label="Total Rows" value={previewResult.summary.total} />
            <SummaryCard label="Ready" value={previewResult.summary.ready} />
            <SummaryCard
              label={hasReadyRows ? 'Will Create' : 'Created'}
              value={
                hasReadyRows
                  ? previewResult.summary.planned_created
                  : previewResult.summary.imported
              }
            />
            <SummaryCard
              label={hasReadyRows ? 'Will Update' : 'Updated'}
              value={
                hasReadyRows ? previewResult.summary.planned_updated : previewResult.summary.updated
              }
            />
            <SummaryCard label="Skipped" value={previewResult.summary.skipped} />
            <SummaryCard label="Invalid" value={previewResult.summary.invalid} />
            <SummaryCard label="Image Issues" value={previewResult.summary.image_failures} />
            <SummaryCard
              label="Banner Skips"
              value={previewResult.summary.collection_banner_warnings}
            />
          </div>

          <WebsiteNavigationCard report={previewResult.website_navigation} />

          <ImageFailuresCard failures={previewResult.image_failures} />

          <Card>
            <CardHeader>
              <CardTitle>Row Preview</CardTitle>
              <CardDescription>
                {previewRowsTruncated
                  ? `Showing a problem-first sample of ${previewRowsReturned.toLocaleString()} of ${previewResult.summary.total.toLocaleString()} rows. Summary counts cover the full file. `
                  : 'Search, filter, and page through the preview before importing. '}
                Deluxe is selected as the default variant when that price point exists. FSN active
                page titles are used as collections when available.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <IndexDataTable
                mode="client"
                pagination
                data={filteredPreviewRows}
                columns={previewColumns}
                getRowId={(row) => String(row.row_number)}
                getRowPresentation={getRowPreviewPresentation}
                defaultPageSize={previewDefaultPageSize}
                maxPageSize={100}
                scrollAreaClassName={
                  previewTableNeedsScrollFrame ? 'h-[min(64rem,calc(100dvh-20rem))]' : undefined
                }
                toolbar={
                  <IndexTableToolbar
                    primary={
                      <IndexTableSearchBox
                        value={previewSearchTerm}
                        onCommit={setPreviewSearchTerm}
                        countText={getPreviewRowCountText(
                          filteredPreviewRows.length,
                          previewRows.length
                        )}
                        placeholder="Search product ID, product, variant, or collection..."
                      />
                    }
                    secondary={
                      <>
                        {hasPreviewFilters ? (
                          <IndexTableClearFiltersButton
                            onClick={() => {
                              setPreviewSearchTerm('')
                              setPreviewStatusFilter('all')
                            }}
                          />
                        ) : null}
                        <Select value={previewStatusFilter} onValueChange={setPreviewStatusFilter}>
                          <SelectTrigger
                            aria-label="Filter preview rows by status"
                            selected={previewStatusFilter !== 'all'}
                            className="w-fit min-w-[11rem]"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {previewStatusFilterOptions.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.icon}
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </>
                    }
                  />
                }
                emptyState={
                  <EmptyState
                    title={hasPreviewFilters ? 'No matching preview rows' : 'No preview rows found'}
                    description={
                      hasPreviewFilters
                        ? 'Try a different search or status filter.'
                        : 'The selected file did not produce any importable product rows.'
                    }
                  />
                }
              />
            </CardContent>
          </Card>
        </>
      ) : null}
    </div>
  )
}
